<script setup>
import { ref, computed, watch, useTemplateRef, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import { useAccount } from 'dashboard/composables/useAccount';
import { generateAutomationPayload } from 'dashboard/helper/automationHelper';
import { validateAutomation } from 'dashboard/helper/validations';
import AutomationRuleFormFields from './AutomationRuleFormFields.vue';
import AutomationFlowCanvas from './AutomationFlowCanvas.vue';
import AutomationFlowNodes from './AutomationFlowNodes.vue';
import {
  AUTOMATION_RULE_EVENTS_ALL,
  AUTOMATION_ACTION_TYPES_ALL,
  AUTOMATION_DASHBOARD_HIDDEN_EVENT_KEYS,
  AUTOMATION_DASHBOARD_HIDDEN_ACTION_KEYS,
} from './constants';

const props = defineProps({
  mode: {
    type: String,
    required: true,
    validator: value => ['create', 'edit'].includes(value),
  },
  automationTypes: {
    type: Object,
    required: true,
  },
  getConditionDropdownValues: {
    type: Function,
    required: true,
  },
  getActionDropdownValues: {
    type: Function,
    required: true,
  },
  appendNewCondition: {
    type: Function,
    required: true,
  },
  appendNewAction: {
    type: Function,
    required: true,
  },
  insertActionAfter: {
    type: Function,
    required: true,
  },
  removeFilter: {
    type: Function,
    required: true,
  },
  removeAction: {
    type: Function,
    required: true,
  },
  resetAction: {
    type: Function,
    required: true,
  },
  onEventChange: {
    type: Function,
    required: true,
  },
});

const emit = defineEmits(['save', 'cancel', 'validationFailed']);
const automation = defineModel('automation', { type: Object, default: null });

const { t } = useI18n();
const { isCloudFeatureEnabled } = useAccount();

const fieldsRef = useTemplateRef('fieldsRef');
const canvasRef = useTemplateRef('canvasRef');
const errors = ref({});
const activePanel = ref('trigger-conditions');
const isPanelOpen = ref(true);

const automationRuleEvents = computed(() => {
  const current = automation.value?.event_name;
  const events = AUTOMATION_RULE_EVENTS_ALL.filter(
    ({ key }) =>
      !AUTOMATION_DASHBOARD_HIDDEN_EVENT_KEYS.has(key) || key === current
  );
  return events.map(event => ({
    ...event,
    value: t(`AUTOMATION.EVENTS.${event.value}`),
  }));
});

const actionKeysInRule = computed(
  () => new Set((automation.value?.actions || []).map(a => a.action_name))
);

const automationActionTypes = computed(() => {
  const types = AUTOMATION_ACTION_TYPES_ALL.filter(
    ({ key }) =>
      !AUTOMATION_DASHBOARD_HIDDEN_ACTION_KEYS.has(key) ||
      actionKeysInRule.value.has(key)
  );
  const actionTypes = isCloudFeatureEnabled('sla')
    ? types
    : types.filter(({ key }) => key !== 'add_sla');

  return actionTypes.map(action => ({
    ...action,
    label: t(`AUTOMATION.ACTIONS.${action.label}`),
  }));
});

watch(
  () => automation.value?.actions?.length,
  (next, prev) => {
    if (next > prev) {
      activePanel.value = `action-${next - 1}`;
    }
  }
);

watch(activePanel, panel => {
  if (panel && !isPanelOpen.value) {
    isPanelOpen.value = true;
  }
});

watch(isPanelOpen, open => {
  if (!open) activePanel.value = '';
  setTimeout(() => canvasRef.value?.centerView?.(), 310);
});

watch(
  () => automation.value,
  () => {
    if (Object.keys(errors.value).length) {
      errors.value = {};
    }
  },
  { deep: true }
);

const history = ref([]);
const historyIndex = ref(-1);
const isRestoring = ref(false);

const snapshot = () => JSON.stringify(automation.value);

const trimHistoryFuture = () => {
  if (historyIndex.value < history.value.length - 1) {
    history.value = history.value.slice(0, historyIndex.value + 1);
  }
};

const MAX_HISTORY = 40;

const pushHistoryDebounced = useDebounceFn(() => {
  if (isRestoring.value || !automation.value) return;
  const snap = snapshot();
  if (history.value.length && snap === history.value[historyIndex.value]) {
    return;
  }
  trimHistoryFuture();
  history.value.push(snap);
  historyIndex.value = history.value.length - 1;
  while (history.value.length > MAX_HISTORY) {
    history.value.shift();
    historyIndex.value -= 1;
  }
}, 320);

watch(
  () => automation.value,
  () => {
    pushHistoryDebounced();
  },
  { deep: true }
);

onMounted(() => {
  if (automation.value) {
    history.value = [snapshot()];
    historyIndex.value = 0;
  }
});

const canUndo = computed(() => historyIndex.value > 0);
const canRedo = computed(
  () => historyIndex.value >= 0 && historyIndex.value < history.value.length - 1
);

const undo = () => {
  if (!canUndo.value) return;
  isRestoring.value = true;
  historyIndex.value -= 1;
  automation.value = JSON.parse(history.value[historyIndex.value]);
  nextTick(() => {
    isRestoring.value = false;
    const m = /^action-(\d+)$/.exec(activePanel.value);
    if (m && Number(m[1]) >= automation.value.actions.length) {
      activePanel.value = 'trigger-conditions';
    }
  });
};

const redo = () => {
  if (!canRedo.value) return;
  isRestoring.value = true;
  historyIndex.value += 1;
  automation.value = JSON.parse(history.value[historyIndex.value]);
  nextTick(() => {
    isRestoring.value = false;
    const m = /^action-(\d+)$/.exec(activePanel.value);
    if (m && Number(m[1]) >= automation.value.actions.length) {
      activePanel.value = 'trigger-conditions';
    }
  });
};

const isConditionsValid = () => {
  return fieldsRef.value?.validateConditions?.() ?? true;
};

const resetValidation = () => {
  errors.value = {};
  fieldsRef.value?.resetConditionRowValidation?.();
};

const emitSaveAutomation = (meta = {}) => {
  fieldsRef.value?.syncCustomAttributeTypesForAutomation?.();
  const conditionsValid = isConditionsValid();
  errors.value = validateAutomation(automation.value);
  if (!conditionsValid || Object.keys(errors.value).length > 0) {
    emit('validationFailed', errors.value);
    return;
  }
  const payload = generateAutomationPayload(automation.value);
  emit('save', payload, props.mode, meta);
};

const handleCancel = () => {
  emit('cancel');
};

defineExpose({
  resetValidation,
  requestSave: emitSaveAutomation,
});
</script>

<template>
  <div
    class="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden"
  >
    <div v-if="automation" class="flex min-h-0 min-w-0 flex-1">
      <AutomationFlowCanvas
        ref="canvasRef"
        class="min-h-0 min-w-0 flex-1"
        :can-undo="canUndo"
        :can-redo="canRedo"
        @undo="undo"
        @redo="redo"
      >
        <AutomationFlowNodes
          v-model:active-panel="activePanel"
          :automation="automation"
          :automation-action-types="automationActionTypes"
          :automation-rule-events="automationRuleEvents"
          @insert-action-after="props.insertActionAfter"
        />
      </AutomationFlowCanvas>

      <div
        class="relative shrink-0 transition-[width] duration-300 ease-out"
        :class="isPanelOpen ? 'overflow-visible' : 'overflow-hidden'"
        :style="{ width: isPanelOpen ? '25%' : '0' }"
      >
        <button
          v-if="isPanelOpen"
          type="button"
          class="absolute start-0 top-7 z-20 flex size-7 -translate-x-1/2 items-center justify-center rounded-lg border border-n-weak bg-n-solid-1 text-n-slate-12 shadow-md transition-colors hover:bg-n-alpha-2 dark:border-n-strong dark:bg-n-solid-2 dark:hover:bg-n-alpha-3 rtl:translate-x-1/2"
          :aria-label="$t('AUTOMATION.EDITOR.CLOSE_PANEL')"
          @click="isPanelOpen = false"
        >
          <span
            class="inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[20px] leading-none ltr:i-lucide-chevron-right rtl:i-lucide-chevron-left"
            aria-hidden="true"
          />
        </button>
        <div
          class="flex h-full min-w-64 flex-col overflow-hidden border-l border-n-weak bg-n-solid-1 shadow-xl dark:border-n-strong dark:bg-n-solid-2"
        >
          <AutomationRuleFormFields
            ref="fieldsRef"
            v-model:automation="automation"
            :mode="mode"
            :active-panel="activePanel"
            :automation-types="automationTypes"
            :automation-rule-events="automationRuleEvents"
            :automation-action-types="automationActionTypes"
            :get-condition-dropdown-values="getConditionDropdownValues"
            :get-action-dropdown-values="getActionDropdownValues"
            :append-new-condition="appendNewCondition"
            :append-new-action="appendNewAction"
            :remove-filter="removeFilter"
            :remove-action="removeAction"
            :reset-action="resetAction"
            :on-event-change="onEventChange"
            :errors="errors"
            @cancel="handleCancel"
          />
        </div>
      </div>
    </div>
  </div>
</template>
